import styled from 'styled-components';
import { Box, Tooltip } from '@mui/material';
import { componentToken } from '../../style/utils';

const HEIGHT_PX = 12;
const CORNER_RX = 3;
const STEP_COUNT = 10;

type Variant = 'wide' | 'compact';

const VARIANT_CONFIG: Record<Variant, { width: number; stepWidth: number }> = {
  wide: { width: 49, stepWidth: 5 },
  compact: { width: 19, stepWidth: 2 },
};

// Ticks divide the bar into STEP_COUNT segments. The 0.5 offset puts a 1px
// SVG stroke on a crisp pixel boundary instead of straddling two pixels.
const getTickPositions = (stepWidth: number): readonly number[] =>
  Array.from({ length: STEP_COUNT - 1 }, (_, i) => (i + 1) * stepWidth - 0.5);

const FillBarOuter = styled(Box).attrs({ component: 'span' })<{
  $variant: Variant;
}>`
  display: block;
  position: relative;
  width: ${({ $variant }) => VARIANT_CONFIG[$variant].width}px;
  height: ${HEIGHT_PX}px;
  border-radius: ${CORNER_RX}px;
  overflow: hidden;
  flex-shrink: 0;
  line-height: 0;
  background-color: ${({ theme }) => theme.palette.surface.divider};
`;

const FillBarInner = styled(Box)<{ $widthPx: number }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${({ $widthPx }) => $widthPx}px;
  background-color: ${({ theme }) => theme.palette.surface.inverse};
`;

const TickOverlay = styled('svg')`
  position: absolute;
  inset: 0;
  display: block;
  pointer-events: none;
`;

const TickLine = styled.line`
  stroke: ${componentToken('fillBar.trackStroke')};
`;

type Props = {
  value: number;
  variant?: Variant;
  tooltipTitle?: string;
};

const FillBar = ({ value, variant = 'wide', tooltipTitle }: Props) => {
  const fraction =
    typeof value === 'number' && !Number.isNaN(value) ? value : 0;
  const clamped = Math.min(1, Math.max(0, fraction));
  const { width, stepWidth } = VARIANT_CONFIG[variant];
  const tickPositions = getTickPositions(stepWidth);

  // Snap the fill to one of 11 discrete steps (0..10) so it always lines up
  // with a tick.
  const filledSteps = Math.min(STEP_COUNT, Math.round(clamped * STEP_COUNT));
  const fillPx = filledSteps * stepWidth;

  return (
    <Tooltip
      title={tooltipTitle}
      enterDelay={300}
      componentsProps={{
        tooltip: { sx: { whiteSpace: 'pre-line' } },
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          lineHeight: 0,
          flexShrink: 0,
          cursor: tooltipTitle ? 'default' : undefined,
        }}
      >
        <FillBarOuter $variant={variant} aria-hidden>
          {fillPx > 0 && <FillBarInner $widthPx={fillPx} />}
          <TickOverlay
            width={width}
            height={HEIGHT_PX}
            viewBox={`0 0 ${width} ${HEIGHT_PX}`}
          >
            {tickPositions.map((x) => (
              <TickLine key={x} x1={x} x2={x} y1={0} y2={HEIGHT_PX} />
            ))}
          </TickOverlay>
        </FillBarOuter>
      </Box>
    </Tooltip>
  );
};

export default FillBar;
